let currentController = null;
let currentTimeoutId = null;
let feedbackHomeParent = null;

/**
 * @param {string} dialogClass
 * @param {string} content
 * @param {number|null} timeout
 * @param {function} callback
 */
export function createFeedbackDialog(dialogClass, content, timeout = 5000, callback = resetFeedbackDialog) {
  const feedbackDialog = document.getElementById('js-feedback-dialog');
  const closeButton = document.getElementById('js-feedback-close');
  const contentEl = document.getElementById('js-feedback-content');

  cancelPending();
  closeIfOpen(feedbackDialog);

  feedbackDialog.className = dialogClass ? `feedback_dialog ${dialogClass}` : 'feedback_dialog';
  setLiveRegionPriority(contentEl, dialogClass === 'feedback_error');

  currentController = new AbortController();
  closeButton.addEventListener('click', () => callback(), { signal: currentController.signal });
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape') {
        callback();
      }
    },
    { signal: currentController.signal },
  );

  if (timeout !== null) {
    currentTimeoutId = setTimeout(() => callback(), timeout);
  }

  // A popover paints above a modal <dialog> only when it is a descendant of that
  // dialog. Re-parent the feedback into the top-most open modal so its content and
  // close button stay clickable instead of being hidden behind the modal on top.
  placeAboveTopmostModal(feedbackDialog);

  showFeedback(feedbackDialog);
  // A live region only announces what changes while it is visible, so the text goes in after the show.
  contentEl.innerHTML = content;
}

/**
 * An error interrupts what the screen reader is saying, anything else waits for a pause.
 *
 * @param {HTMLElement} contentEl
 * @param {boolean} isError
 */
function setLiveRegionPriority(contentEl, isError) {
  contentEl.setAttribute('role', isError ? 'alert' : 'status');
  contentEl.setAttribute('aria-live', isError ? 'assertive' : 'polite');
}

/**
 * @param {HTMLElement} feedbackDialog
 */
function showFeedback(feedbackDialog) {
  if (typeof feedbackDialog.showPopover === 'function') {
    feedbackDialog.showPopover();
  } else {
    feedbackDialog.show();
  }
}

/**
 * createFeedbackDialog for a caller that must not move on before the customer could read it.
 *
 * @returns {Promise<void>} settles once the dialog is closed, by the customer or the timeout
 */
export function createFeedbackDialogUntilClosed(dialogClass, content, timeout = 5000) {
  return new Promise((resolve) => {
    createFeedbackDialog(dialogClass, content, timeout, () => {
      resetFeedbackDialog();
      resolve();
    });
  });
}

/**
 * @param {{error_csrf: string}} res
 */
export function createCsrfFeedbackDialog(res) {
  createFeedbackDialog('feedback_error', res.error_csrf, null, () => location.reload());
}

function resetFeedbackDialog() {
  const feedbackDialog = document.getElementById('js-feedback-dialog');
  cancelPending();
  closeIfOpen(feedbackDialog);
  document.getElementById('js-feedback-content').innerHTML = '';
  restoreFeedbackHome(feedbackDialog);
}

/**
 * Moves the feedback dialog into the top-most open modal dialog (if any), so the
 * popover stacks above it. Falls back to its original location otherwise.
 *
 * @param {HTMLElement} feedbackDialog
 */
function placeAboveTopmostModal(feedbackDialog) {
  if (feedbackHomeParent === null) {
    feedbackHomeParent = feedbackDialog.parentElement;
  }

  const modal = getTopmostModalDialog();
  const targetParent = modal && modal !== feedbackDialog ? modal : feedbackHomeParent;
  if (targetParent && feedbackDialog.parentElement !== targetParent) {
    targetParent.appendChild(feedbackDialog);
  }

  // A closed <dialog> is display:none, which would hide the feedback nested inside
  // it (e.g. when the host modal closes right after a success message is shown).
  // Move the feedback back home and re-show it so it survives the modal closing.
  if (modal && modal !== feedbackDialog) {
    modal.addEventListener(
      'close',
      () => {
        restoreFeedbackHome(feedbackDialog);
        showFeedback(feedbackDialog);
      },
      { signal: currentController.signal, once: true },
    );
  }
}

/**
 * @param {HTMLElement} feedbackDialog
 */
function restoreFeedbackHome(feedbackDialog) {
  if (feedbackHomeParent && feedbackDialog.parentElement !== feedbackHomeParent) {
    feedbackHomeParent.appendChild(feedbackDialog);
  }
}

/**
 * @returns {HTMLDialogElement|null}
 */
function getTopmostModalDialog() {
  let modalDialogs;
  try {
    modalDialogs = document.querySelectorAll('dialog:modal');
  } catch {
    // The :modal selector is unsupported in older browsers, which also lack the
    // Popover API and therefore never run into the stacking issue this guards against.
    return null;
  }

  // Stacked modals are not combined with feedback dialogs in practice, so the last
  // modal in DOM order is a sufficient proxy for the top-most one.
  return modalDialogs.length ? modalDialogs[modalDialogs.length - 1] : null;
}

function cancelPending() {
  currentController?.abort();
  currentController = null;
  if (currentTimeoutId !== null) {
    clearTimeout(currentTimeoutId);
    currentTimeoutId = null;
  }
}

function closeIfOpen(feedbackDialog) {
  if (typeof feedbackDialog.hidePopover === 'function' && feedbackDialog.matches(':popover-open')) {
    feedbackDialog.hidePopover();
  } else if (feedbackDialog.open) {
    feedbackDialog.close();
  }
}

// Backward-compat for the global (script-loader) scripts that call these
// without an import. Remove once those callers are migrated to ES modules.
window.createFeedbackDialog = createFeedbackDialog;
window.createCsrfFeedbackDialog = createCsrfFeedbackDialog;
